import React, { useEffect } from "react";
import { Box, Typography, Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";

import LoginLayout from "../../layouts/LoginLayout";
import { useNavigate, useSearchParams } from "react-router-dom";
import { InputReactHookForm } from "../../components/input/InputReactHookForm";
import alertService from "../../components/alert/alertService";
import UsersService from "../../service/users.service";
import { useAuth } from "../../contexts/UserContext";

import { common } from "../../i18n";
import { LOGO_IMG_SRC } from "../../constants";

type ResetPasswordFormValues = {
  password: string;
  repassword: string;
};

const ResetPasswordPage: React.FC = () => {
  const { t } = useTranslation([common]);
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const {
    handleSubmit,
    control,
    watch,
    formState: { isValid, isSubmitting },
  } = useForm<ResetPasswordFormValues>({
    mode: "all",
    reValidateMode: "onChange",
    delayError: 400,
  });

  useEffect(() => {
    if (!token) {
      navigate("/forgot-password", { replace: true });
    }
  }, [token, navigate]);

  if (!token) {
    return null;
  }

  const onSubmit = async (values: ResetPasswordFormValues) => {
    if (!token) {
      alertService.error("ไม่พบ Token สำหรับรีเซ็ตรหัสผ่าน");
      return;
    }

    let result;
    try {
      result = await UsersService.recoveryPassword(
        token,
        values.repassword,
      );
    } catch {
      // error alert already shown by service
      return;
    }

    if (result) {
      await alertService.success(
        t("Reset-Password-Success"),
        t("Reset-Password"),
      );
      logout();
      navigate("/login");
    }
  };

  return (
    <LoginLayout>
      <Box
        maxWidth={"400px"}
        display={"flex"}
        flexDirection={"column"}
        rowGap={"20px"}
        width={"100%"}
      >
        <img
          src={LOGO_IMG_SRC}
          alt="logo"
          style={{
            padding: "12px 0px",
            height: "28px",
            width: "233px",
            marginBottom: "20px",
          }}
        />

        <Typography variant="h1">{t("Reset-Password")}</Typography>

        <form onSubmit={handleSubmit(onSubmit)}>
          <Box
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "20px",
              width: "100%",
            }}
            padding={"24px 0px"}
          >
            <InputReactHookForm
              required
              rules={{
                required: t("Password-must-be-at-least-6-characters"),
                pattern: {
                  value:
                    /^(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9])(?=.*[\W_]).{6,20}$/,
                  message: t("password-fails-to-match-pattern"),
                },
              }}
              control={control}
              name={"password"}
              label={t("Password")}
              info={t("password-fails-to-match-pattern")}
              placeholder={t("Password")}
              type="password"
            />

            <InputReactHookForm
              required
              rules={{
                required: t("Password-must-be-at-least-6-characters"),
                validate: () => {
                  if (watch("password") !== watch("repassword")) {
                    return t("password-and-repassword-not-equal");
                  }
                  return true;
                },
              }}
              control={control}
              name={"repassword"}
              label={t("Confirm-Your-New-Password")}
              info={t("Confirm-Your-New-Password")}
              placeholder={t("Confirm-Your-New-Password")}
              type="password"
            />
          </Box>

          <Button
            fullWidth
            type="submit"
            variant="contained"
            color={"secondary"}
            disabled={!isValid || isSubmitting}
            sx={{
              marginBottom: "20px",
            }}
          >
            {t("Confirm")}
          </Button>
        </form>
      </Box>
    </LoginLayout>
  );
};

export default ResetPasswordPage;
